'use client'

import { useState, useEffect } from 'react'
import { useDebounce } from '../../hooks/useDebounce'
import Input from '@/components/ui/input'
import Button from '@/components/ui/button'
import Checkbox from '@/components/ui/checkbox'

interface EventSearchProps {
  onSearch: (filters: EventSearchFilters) => void
  className?: string
}

export interface EventSearchFilters {
  query: string
  startDate?: string
  endDate?: string
  skillLevel?: string
  isPrivate?: boolean
  hostId?: string
  gameId?: string
}

function EventSearch({ onSearch, className = '' }: EventSearchProps) {
  const [filters, setFilters] = useState<EventSearchFilters>({
    query: '',
    startDate: '',
    endDate: '',
    skillLevel: '',
    isPrivate: undefined,
    hostId: '',
    gameId: ''
  })
  
  const debouncedQuery = useDebounce(filters.query, 500)
  
  // Update filters and trigger search
  const handleFilterChange = (key: keyof EventSearchFilters, value: any) => {
    const updatedFilters = { ...filters, [key]: value }
    setFilters(updatedFilters)
    
    // If it's the query field, let the debounce handle it
    if (key !== 'query') {
      onSearch(updatedFilters)
    }
  }
  
  // Handle query debounce
  useEffect(() => {
    if (debouncedQuery !== undefined) {
      onSearch(filters)
    }
  }, [debouncedQuery, filters, onSearch])
  
  // Clear all filters
  const handleClearFilters = () => {
    const resetFilters = {
      query: '',
      startDate: '',
      endDate: '',
      skillLevel: '',
      isPrivate: undefined,
      hostId: '',
      gameId: ''
    }
    setFilters(resetFilters)
    onSearch(resetFilters)
  }
  
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col md:flex-row gap-3">
        {/* Search query input */}
        <div className="flex-grow">
          <Input
            placeholder="Search events..."
            value={filters.query}
            onChange={(e) => handleFilterChange('query', e.target.value)}
            className="w-full"
          />
        </div>
        
        {/* Date range inputs */}
        <div className="flex gap-2">
          <Input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            placeholder="From"
            className="w-full md:w-32"
          />
          <Input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            placeholder="To"
            className="w-full md:w-32"
          />
        </div>
        
        {/* Skill level filter */}
        <div>
          <select
            value={filters.skillLevel}
            onChange={(e) => handleFilterChange('skillLevel', e.target.value)}
            className="w-full md:w-40 rounded-md border border-gray-700 bg-background px-3 py-2 text-white"
          >
            <option value="">All Skill Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="all_levels">All Levels</option>
          </select>
        </div>
        
        {/* Clear filters button */}
        <Button
          variant="outline"
          onClick={handleClearFilters}
          className="whitespace-nowrap"
        >
          Clear Filters
        </Button>
      </div>
      
      {/* Additional filters */}
      <div className="flex items-center gap-4 text-sm text-gray-300">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={filters.isPrivate === true}
            onChange={(e) => handleFilterChange('isPrivate', e.target.checked ? true : undefined)}
          />
          Show private events
        </label>
        
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={filters.isPrivate === false}
            onChange={(e) => handleFilterChange('isPrivate', e.target.checked ? false : undefined)}
          />
          Show public events only
        </label>
      </div>
    </div>
  )
}

export { EventSearch }
